import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const MODEL_OPTIONS = [
  { key: "anthropic", label: "Claude Sonnet 5.5 (recommended)" },
  { key: "openai", label: "GPT 6 Sol" },
  { key: "gemini", label: "Gemini 3.1 Pro" },
];

export default function BioWriterDialog({ open, onOpenChange, name, subjects, onApply }) {
  const [bullets, setBullets] = useState("");
  const [model, setModel] = useState("anthropic");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);

  const generate = async () => {
    if (!bullets.trim()) {
      toast.error("Please add a few bullet points first");
      return;
    }
    setLoading(true);
    setDraft(null);
    try {
      const { data } = await api.post("/ai/bio-writer", {
        model,
        name: name || "Tutor",
        bullets,
        subjects: subjects || [],
      });
      setDraft({ headline: data.headline, bio: data.bio });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI call failed");
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!draft) return;
    onApply?.(draft);
    toast.success("Bio applied — remember to save your profile");
    onOpenChange(false);
    setDraft(null);
    setBullets("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="bio-writer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> AI Bio Writer
          </DialogTitle>
          <DialogDescription>
            Jot a few facts about yourself — we'll draft a warm, professional headline and bio.
            Pick the AI you'd like to use.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>AI model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="bio-writer-model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODEL_OPTIONS.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Your notes</Label>
            <Textarea
              rows={5}
              value={bullets}
              onChange={(e) => setBullets(e.target.value)}
              placeholder={"e.g.\n- Native French speaker from Lyon\n- 6 years tutoring, DELF/DALF prep\n- Sorbonne MA in Linguistics\n- Love film-based lessons"}
              data-testid="bio-writer-bullets"
            />
          </div>

          {draft && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="bio-writer-draft">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Headline
                </p>
                <p className="font-medium">{draft.headline}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Bio
                </p>
                <p className="text-sm text-muted-foreground">{draft.bio}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={generate}
            disabled={loading}
            className="rounded-full"
            data-testid="bio-writer-generate-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Drafting…
              </>
            ) : draft ? (
              "Try another draft"
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-1.5" /> Generate
              </>
            )}
          </Button>
          <Button
            type="button"
            onClick={apply}
            disabled={!draft || loading}
            className="rounded-full"
            data-testid="bio-writer-apply-button"
          >
            Apply to profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
